import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Play, 
  Sparkles, 
  Zap, 
  Music, 
  Type, 
  Palette, 
  Download,
  Check,
  ArrowRight,
  Video,
  Wand2,
  Clock
} from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Video className="w-6 h-6" />,
      title: "Smart Upload",
      description: "Upload any video format. Our AI handles the rest."
    },
    {
      icon: <Wand2 className="w-6 h-6" />,
      title: "Auto Editing",
      description: "Intelligent cuts, transitions, and scene detection."
    },
    {
      icon: <Music className="w-6 h-6" />,
      title: "Music Matching",
      description: "AI-suggested background music that fits your content."
    },
    {
      icon: <Type className="w-6 h-6" />,
      title: "Auto Captions",
      description: "Generate accurate captions in multiple styles."
    },
    {
      icon: <Palette className="w-6 h-6" />,
      title: "Color Grading",
      description: "Professional color correction with one click."
    },
    {
      icon: <Download className="w-6 h-6" />,
      title: "Export Anywhere",
      description: "Optimized exports for all social platforms."
    }
  ];

  const plans = [
    {
      name: "Free",
      price: "0",
      period: "forever",
      features: ["2 projects", "720p export", "Basic captions", "Standard processing"],
      cta: "Get Started",
      popular: false
    },
    {
      name: "Pro",
      price: "15",
      period: "month",
      features: ["Unlimited projects", "4K export", "AI captions", "Premium templates", "Priority processing", "API access"],
      cta: "Start Free Trial",
      popular: true
    },
    {
      name: "Annual",
      price: "144",
      period: "year",
      features: ["Everything in Pro", "2 months free", "Custom branding", "Team collaboration", "Dedicated support"],
      cta: "Save 20%",
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-[#030303] overflow-hidden">
      {/* Noise overlay */}
      <div className="noise-overlay" />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#CCFF00] flex items-center justify-center">
              <Play className="w-4 h-4 text-black fill-black" />
            </div>
            <span className="font-heading font-bold text-xl text-white">DIDIT</span>
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-white/70 hover:text-white transition-colors">Features</a>
            <a href="#pricing" className="text-white/70 hover:text-white transition-colors">Pricing</a>
            <a href="#how-it-works" className="text-white/70 hover:text-white transition-colors">How it Works</a>
          </div>
          
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              className="text-white/70 hover:text-white hover:bg-white/5"
              onClick={() => navigate('/login')}
              data-testid="nav-login-btn"
            >
              Log in
            </Button>
            <Button 
              className="bg-[#CCFF00] text-black hover:bg-[#B3E600] rounded-full font-semibold px-6"
              onClick={() => navigate('/register')}
              data-testid="nav-signup-btn"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6">
        <div className="hero-glow absolute inset-0" />
        
        <div className="max-w-7xl mx-auto relative">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8">
              <Sparkles className="w-4 h-4 text-[#CCFF00]" />
              <span className="text-sm text-white/70">AI-Powered Video Editing</span>
            </div>
            
            <h1 className="font-marketing font-extrabold text-5xl md:text-7xl lg:text-8xl text-white tracking-tighter leading-none mb-6">
              Edit Videos<br />
              <span className="text-[#CCFF00]">Like Magic</span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto mb-10 leading-relaxed">
              Transform raw footage into professional videos in minutes. 
              No skills required. Just upload and let AI do the heavy lifting.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg"
                className="bg-[#CCFF00] text-black hover:bg-[#B3E600] rounded-full font-semibold px-8 py-6 text-lg btn-primary-glow transition-transform hover:scale-105 active:scale-95"
                onClick={() => navigate('/register')}
                data-testid="hero-cta-btn"
              >
                Start Creating Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="bg-white/5 text-white hover:bg-white/10 border-white/10 rounded-full px-8 py-6 text-lg"
                data-testid="hero-demo-btn"
              >
                <Play className="w-5 h-5 mr-2" />
                Watch Demo
              </Button>
            </div>
          </motion.div>

          {/* Hero Image/Preview */}
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-20 relative"
          >
            <div className="aspect-video rounded-2xl overflow-hidden border border-white/10 bg-[#0A0A0A] shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1727142073871-d40f5a7c76d8?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Video editing interface"
                className="w-full h-full object-cover opacity-80"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-transparent to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-[#CCFF00]/20 backdrop-blur-xl flex items-center justify-center cursor-pointer hover:scale-110 transition-transform">
                  <Play className="w-8 h-8 text-[#CCFF00] fill-[#CCFF00]" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it Works */}
      <section id="how-it-works" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.2em] text-[#CCFF00] font-semibold">How it Works</span>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-white mt-4 tracking-tight">
              Three Steps to Pro Videos
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Upload", desc: "Drop your raw footage and optional external audio" },
              { step: "02", title: "Customize", desc: "Choose your style, captions, and music preferences" },
              { step: "03", title: "Export", desc: "Download your professionally edited video" }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.2 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="text-8xl font-marketing font-extrabold text-white/5 absolute -top-6 -left-2">
                  {item.step}
                </div>
                <Card className="bg-[#0A0A0A] border-white/10 p-8 rounded-2xl relative card-hover">
                  <div className="w-12 h-12 rounded-xl bg-[#CCFF00]/10 flex items-center justify-center mb-6">
                    {i === 0 && <Video className="w-6 h-6 text-[#CCFF00]" />}
                    {i === 1 && <Wand2 className="w-6 h-6 text-[#CCFF00]" />}
                    {i === 2 && <Download className="w-6 h-6 text-[#CCFF00]" />}
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-white mb-3">{item.title}</h3>
                  <p className="text-white/60">{item.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 px-6 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.2em] text-[#CCFF00] font-semibold">Features</span>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-white mt-4 tracking-tight">
              Everything You Need
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="bg-[#121212] border-white/5 p-6 rounded-xl card-hover h-full">
                  <div className="w-10 h-10 rounded-lg bg-[#CCFF00]/10 flex items-center justify-center mb-4 text-[#CCFF00]">
                    {feature.icon}
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-white mb-2">{feature.title}</h3>
                  <p className="text-white/50 text-sm">{feature.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.2em] text-[#CCFF00] font-semibold">Pricing</span>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-white mt-4 tracking-tight">
              Simple, Transparent Pricing
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.15 }}
                viewport={{ once: true }}
              >
                <Card 
                  className={`p-8 rounded-2xl h-full flex flex-col ${
                    plan.popular 
                      ? 'bg-[#CCFF00] text-black border-[#CCFF00]' 
                      : 'bg-[#0A0A0A] border-white/10 text-white'
                  }`}
                  data-testid={`pricing-card-${plan.name.toLowerCase()}`}
                >
                  {plan.popular && (
                    <div className="text-xs uppercase tracking-wider font-semibold mb-4 flex items-center gap-2">
                      <Zap className="w-4 h-4" />
                      Most Popular
                    </div>
                  )}
                  <h3 className="font-heading font-bold text-2xl mb-2">{plan.name}</h3>
                  <div className="mb-6">
                    <span className="font-marketing font-bold text-5xl">CHF {plan.price}</span>
                    <span className={plan.popular ? 'text-black/60' : 'text-white/60'}>/{plan.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8 flex-grow">
                    {plan.features.map((feature, j) => (
                      <li key={j} className="flex items-center gap-3">
                        <Check className={`w-5 h-5 ${plan.popular ? 'text-black' : 'text-[#CCFF00]'}`} />
                        <span className={plan.popular ? 'text-black/80' : 'text-white/70'}>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full rounded-full font-semibold py-6 ${
                      plan.popular 
                        ? 'bg-black text-white hover:bg-black/80' 
                        : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                    }`}
                    onClick={() => navigate('/register')}
                    data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                  >
                    {plan.cta}
                  </Button>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#0A0A0A] to-[#121212] border border-white/10 rounded-3xl p-12 md:p-16"
          >
            <Clock className="w-12 h-12 text-[#CCFF00] mx-auto mb-6" />
            <h2 className="font-marketing font-bold text-4xl md:text-5xl text-white tracking-tight mb-4">
              Stop Wasting Time on Editing
            </h2>
            <p className="text-white/60 text-lg mb-8 max-w-xl mx-auto">
              Join thousands of creators who save hours every week with DIDIT Studio.
            </p>
            <Button 
              size="lg"
              className="bg-[#CCFF00] text-black hover:bg-[#B3E600] rounded-full font-semibold px-10 py-6 text-lg btn-primary-glow transition-transform hover:scale-105"
              onClick={() => navigate('/register')}
              data-testid="cta-signup-btn"
            >
              Start Creating Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#CCFF00] flex items-center justify-center">
              <Play className="w-4 h-4 text-black fill-black" />
            </div>
            <span className="font-heading font-bold text-xl text-white">DIDIT Studio</span>
          </div>
          <p className="text-white/40 text-sm">
            © 2025 DIDIT Studio. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
